import {useState} from "react";
import {postToApi, putToApi, deleteFromApi, getFromApi} from "../../api/auth";

export default function LinkedTodo({folderId, todo, allTodoGroups, onRefresh, linkApiPath}) {
    const [linking, setLinking] = useState(false);
    const [selectedId, setSelectedId] = useState("");
    const [newListName, setNewListName] = useState("");
    const [itemName, setItemName] = useState("");
    const [creatingNew, setCreatingNew] = useState(false);

    const handleLink = async (e) => {
        e.preventDefault();
        if (!selectedId) return;
        await putToApi(linkApiPath || `api/notes/${folderId}/link-todo`, {todo_id: selectedId});
        setLinking(false);
        setSelectedId("");
        onRefresh();
    };

    const handleCreateAndLink = async (e) => {
        e.preventDefault();
        if (!newListName.trim()) return;
        await postToApi("api/todo", {name: newListName.trim()});
        const lists = await getFromApi("api/todo");
        const created = lists.data?.find(g => g.name === newListName.trim());
        if (created) {
            await putToApi(linkApiPath || `api/notes/${folderId}/link-todo`, {todo_id: String(created._id)});
        }
        setNewListName("");
        setCreatingNew(false);
        onRefresh();
    };

    const handleUnlink = async () => {
        await putToApi(linkApiPath || `api/notes/${folderId}/link-todo`, {todo_id: null});
        onRefresh();
    };

    const handleAddItem = async (e) => {
        e.preventDefault();
        if (!itemName.trim() || !todo) return;
        await postToApi(`api/todo/${todo._id}/items`, {name: itemName.trim()});
        setItemName("");
        onRefresh();
    };

    const handleComplete = async (itemId) => {
        await putToApi(`api/todo/${todo._id}/items/${itemId}`, {});
        onRefresh();
    };

    const handleDeleteItem = async (itemId) => {
        await deleteFromApi(`api/todo/${todo._id}/items/${itemId}`);
        onRefresh();
    };

    /* ── No linked todo ── */
    if (!todo) {
        const unlinked = allTodoGroups.filter(g => true);
        return (
            <div className="panel mt-3" style={{borderTop: "3px solid #e5e7eb"}}>
                <div className="d-flex align-items-center justify-content-between mb-3">
                    <span className="fw-semibold small"><i className="bi bi-check2-square me-2 text-success" />Todo</span>
                    <div className="d-flex gap-2">
                        <button className="btn btn-sm btn-outline-secondary" onClick={() => { setLinking(true); setCreatingNew(false); }}>
                            Link existing
                        </button>
                        <button className="btn btn-sm btn-dark" onClick={() => { setCreatingNew(true); setLinking(false); }}>
                            <i className="bi bi-plus-lg me-1" />New list
                        </button>
                    </div>
                </div>

                {linking && (
                    <form onSubmit={handleLink} className="d-flex gap-2">
                        <select className="form-select form-select-sm" value={selectedId} onChange={e => setSelectedId(e.target.value)} required>
                            <option value="">Select a list...</option>
                            {unlinked.map(g => <option key={g._id} value={g._id}>{g.name}</option>)}
                        </select>
                        <button type="submit" className="btn btn-sm btn-dark">Link</button>
                        <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => setLinking(false)}>Cancel</button>
                    </form>
                )}

                {creatingNew && (
                    <form onSubmit={handleCreateAndLink} className="d-flex gap-2">
                        <input
                            className="form-control form-control-sm"
                            placeholder="New list name..."
                            value={newListName}
                            onChange={e => setNewListName(e.target.value)}
                            required
                            autoFocus
                        />
                        <button type="submit" className="btn btn-sm btn-dark">Create</button>
                        <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => setCreatingNew(false)}>Cancel</button>
                    </form>
                )}

                {!linking && !creatingNew && (
                    <p className="text-muted small mb-0">No todo list linked to this folder.</p>
                )}
            </div>
        );
    }

    /* ── Linked todo ── */
    const ongoing = todo.items?.filter(i => !i.is_completed) || [];
    const done = todo.items?.filter(i => i.is_completed) || [];

    return (
        <div className="panel mt-3" style={{borderTop: "3px solid #10b981"}}>
            <div className="d-flex align-items-center justify-content-between mb-3">
                <span className="fw-semibold small">
                    <i className="bi bi-check2-square me-2 text-success" />
                    {todo.name}
                    <span className="badge text-bg-warning text-dark ms-2">{ongoing.length} left</span>
                    <span className="badge text-bg-success ms-1">{done.length} done</span>
                </span>
                <button className="btn btn-sm btn-outline-secondary" onClick={handleUnlink} title="Unlink todo list">
                    <i className="bi bi-x-lg" />
                </button>
            </div>

            <form onSubmit={handleAddItem} className="d-flex gap-2 mb-3">
                <input
                    className="form-control form-control-sm"
                    placeholder="Add a task..."
                    value={itemName}
                    onChange={e => setItemName(e.target.value)}
                />
                <button type="submit" className="btn btn-sm btn-dark flex-shrink-0">Add</button>
            </form>

            <div style={{maxHeight: 260, overflowY: "auto"}}>
                {ongoing.map(item => (
                    <div key={item._id} className="d-flex align-items-center gap-2 py-1 border-bottom">
                        <button
                            className="btn btn-sm p-0 border-0 text-muted"
                            style={{width: 22, height: 22, borderRadius: "50%", border: "2px solid #d1d5db !important"}}
                            onClick={() => handleComplete(item._id)}
                            title="Mark done"
                        >
                            <i className="bi bi-circle" style={{fontSize: "1rem", color: "#d1d5db"}} />
                        </button>
                        <span className="flex-grow-1 small">{item.name}</span>
                        <button className="btn btn-sm p-0 border-0 text-muted" onClick={() => handleDeleteItem(item._id)}>
                            <i className="bi bi-x" />
                        </button>
                    </div>
                ))}
                {done.map(item => (
                    <div key={item._id} className="d-flex align-items-center gap-2 py-1 border-bottom" style={{opacity: 0.5}}>
                        <i className="bi bi-check-circle-fill text-success" style={{fontSize: "1rem"}} />
                        <span className="flex-grow-1 small text-decoration-line-through text-muted">{item.name}</span>
                        <button className="btn btn-sm p-0 border-0 text-muted" onClick={() => handleDeleteItem(item._id)}>
                            <i className="bi bi-x" />
                        </button>
                    </div>
                ))}
                {!todo.items?.length && <p className="text-muted small mb-0">No tasks yet.</p>}
            </div>
        </div>
    );
}
